import type { Agent, Status } from '@studio/models/agent';

/** Proxy liveness verdict owned by C-1 probe machinery. */
export type LivenessVerdict = 'alive' | 'suspect' | 'dead';

type AgentDraft = {
  agents: Record<string, { status?: Status } | undefined>;
};

type ProxyErrorInput = {
  embedded: boolean;
  getLivenessVerdict: () => LivenessVerdict;
  id: Agent['id'];
};

type ConnectionErrorInput = {
  inRoomUnsafe: (identity: Agent['id']) => boolean;
  isConnectionAlive: () => boolean;
  pingAgent: () => Promise<boolean>;
};

/**
 * Depot write for proxy `state.error`.
 *
 * When the proxy already declared `dead`, skip a second `pingAgent()` — the
 * probe cap was the liveness decision; re-pinging adds up to 10s and can
 * write `idle` while `getStatus()` is still `offline`.
 */
export async function handleProxyStateError(
  proxy: ProxyErrorInput,
  conn: ConnectionErrorInput,
  producer: (recipe: (draft: AgentDraft) => void) => void
): Promise<void> {
  const agentId = proxy.id;

  if (proxy.getLivenessVerdict() === 'dead') {
    writeDepotStatus(producer, agentId, proxy.embedded ? 'offline-embedded' : 'offline');
    return;
  }

  const inRoom = conn.inRoomUnsafe(agentId);
  const isAlive = conn.isConnectionAlive();
  const canPing = inRoom && isAlive && (await conn.pingAgent());

  const status: Status =
    inRoom && isAlive && canPing ? 'idle' : proxy.embedded ? 'offline-embedded' : 'offline';

  writeDepotStatus(producer, agentId, status);
}

function writeDepotStatus(
  producer: (recipe: (draft: AgentDraft) => void) => void,
  agentId: Agent['id'],
  status: Status
): void {
  producer((draft) => {
    if (draft.agents[agentId]?.status === 'idle') {
      return;
    }

    // web boot recovery: skip if the depot entry is not yet present.
    if (draft.agents[agentId] !== undefined) {
      draft.agents[agentId]!.status = status;
    }
  });
}
